import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryFilters,
  QueryKey,
  SetDataOptions,
  Updater,
  onlineManager,
} from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

export enum QueryPrefix {
  assignmentAttempts = 'assignmentAttempts',
  myUnsavedPrompt = 'myUnsavedPrompt',
  threads = 'threads',
  lectureNotes = 'lectureNotes',
  summaries = 'summaries',
  myMessages = 'myMessages',
  accessCodes = 'accessCodes',
  assignmentQuestions = 'assignmentQuestions',
  myAssignments = 'myAssignments',
  assignmentGrades = 'assignmentGrades',
  myCourseSections = 'myCourseSections',
  myNotes = 'myNotes',
  myBookmarks = 'myBookmarks',
  companies = 'companies',
  enrollment = 'enrollment',
}

export enum MutationMethod {
  create = 'create',
  update = 'update',
  delete = 'delete',
}

export type MutationKey = `${QueryPrefix}-${MutationMethod}`;

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      enqueueSnackbar({ variant: 'error', message: error.message });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.options.meta?.suppressSnackbar) {
        return;
      }

      enqueueSnackbar({ variant: 'error', message: error.message });
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: import.meta.env.MODE === 'production',
    },
  },
});

export const isOnline = () => onlineManager.isOnline();

export function setQueryDataIfOnline<TData>(
  queryKey: QueryKey,
  updater: Updater<TData | undefined, TData | undefined>,
  options?: SetDataOptions
) {
  if (!isOnline()) {
    return undefined;
  }

  return queryClient.setQueryData<TData | undefined>(queryKey, updater, options);
}

export function setQueriesDataIfOnline<TData>(
  filters: QueryFilters,
  updater: Updater<TData | undefined, TData | undefined>,
  options?: SetDataOptions
) {
  if (!isOnline()) {
    return [];
  }

  return queryClient.setQueriesData<TData | undefined>(filters, updater, options);
}
